import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import AppSidebar, { MobileTopBar } from "@/components/AppSidebar";
import { BookOpen, CalendarDays, History, Settings, ChevronRight, Check } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";

const formatGuideDate = (str) => {
  if (!str) return "";
  const d = parseISO(str);
  return isValid(d) ? format(d, "MMM d, yyyy") : str;
};

export default function GuideArchive({ user }) {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [completion, setCompletion] = useState({});
  const [loading, setLoading] = useState(true);

  const guideNavItems = [
    { icon: CalendarDays, label: "This Week", active: false, onClick: () => navigate("/guide") },
    { icon: History, label: "Past Guides", active: true, onClick: () => {} },
    ...(user?.role === "teacher"
      ? [{ icon: Settings, label: "Manage Guides", active: false, onClick: () => navigate("/guide/admin") }]
      : []),
  ];

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/guides`),
      axios.get(`${API}/guides/completion-summary`),
    ]).then(([guidesRes, completionRes]) => {
      setGuides(guidesRes.data);
      setCompletion(completionRes.data || {});
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar user={user} active="guide" brandLabel="Weekly Guide" navItems={guideNavItems} />
      <MobileTopBar brandLabel="Weekly Guide" />
      <div className="md:ml-[68px] xl:ml-[260px] pt-14 md:pt-0">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Past Guides</h1>
            <p className="text-sm text-slate-500 mt-0.5">Revisit any previous week's devotionals and sermon notes.</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-500 rounded-full animate-spin" />
            </div>
          ) : guides.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
              <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No guides yet</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="guide-archive-list">
              {guides.map(g => {
                const c = completion[g.guide_id];
                return (
                <button
                  key={g.guide_id}
                  data-testid={`guide-archive-item-${g.guide_id}`}
                  onClick={() => navigate(`/guide?guide_id=${g.guide_id}`)}
                  className="w-full bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3 text-left hover:border-emerald-300 hover:shadow-sm transition-all"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-800 truncate">{g.week_label}</p>
                      {!g.is_published && (
                        <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">Draft</Badge>
                      )}
                      {c && c.all_done && (
                        <Badge
                          data-testid={`guide-complete-badge-${g.guide_id}`}
                          className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs"
                        >
                          <Check className="w-3 h-3 mr-1" /> All Done
                        </Badge>
                      )}
                    </div>
                    {g.theme && <p className="text-xs text-emerald-600 mt-0.5">{g.theme}</p>}
                    <p className="text-xs text-slate-400 mt-0.5">{formatGuideDate(g.week_start)} &rarr; {formatGuideDate(g.week_end)}</p>
                    {c && !c.all_done && (
                      <p className="text-xs text-slate-400 mt-1" data-testid={`guide-progress-${g.guide_id}`}>
                        {c.completed}/{c.total} days completed
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
